/**
 * In-app Help menu (QA F4): replay the guided tour, open the FAQ, send
 * feedback. Opened from the shell's Help entry (sidebar on desktop, header
 * icon on phones).
 */
import React from 'react';
import { Linking, Pressable, StyleSheet, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { Sheet } from '@/components/ui/Sheet';
import { reviewDesk } from '../../design-system/theme';
import { useTutorial } from '../../context/TutorialContext';
import { useTranslation } from '../../hooks/useTranslation';
import { helpUrlFor } from '../layout/AppHeaderBar';
import { isFeedbackAvailable, openFeedback } from '../feedback/feedbackLauncher';

interface HelpMenuProps {
  visible: boolean;
  onClose: () => void;
}

interface HelpRowProps {
  testID: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  title: string;
  description: string;
  onPress: () => void;
}

function HelpRow({ testID, icon, title, description, onPress }: HelpRowProps) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
    >
      <View style={styles.rowIcon}>
        <MaterialIcons name={icon} size={22} color={reviewDesk.accent} />
      </View>
      <View style={styles.rowText}>
        <Text style={styles.rowTitle}>{title}</Text>
        <Text style={styles.rowDescription}>{description}</Text>
      </View>
      <MaterialIcons name="chevron-right" size={20} color={reviewDesk.muted} />
    </Pressable>
  );
}

export function HelpMenu({ visible, onClose }: HelpMenuProps) {
  const { t, language } = useTranslation();
  const { startTour } = useTutorial();

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('helpMenu.title')}
      closeLabel={t('common.close')}
      testID="help_menu"
      maxWidth={460}
    >
      <View style={styles.list}>
        <HelpRow
          testID="help_menu_tour"
          icon="tour"
          title={t('helpMenu.tour')}
          description={t('helpMenu.tourDescription')}
          onPress={() => {
            onClose();
            // Let the sheet close before the overlay measures the page.
            setTimeout(() => startTour('help'), 250);
          }}
        />
        <HelpRow
          testID="help_menu_faq"
          icon="menu-book"
          title={t('helpMenu.faq')}
          description={t('helpMenu.faqDescription')}
          onPress={() => {
            onClose();
            void Linking.openURL(helpUrlFor(language));
          }}
        />
        {isFeedbackAvailable() && (
          <HelpRow
            testID="help_menu_feedback"
            icon="feedback"
            title={t('helpMenu.feedback')}
            description={t('helpMenu.feedbackDescription')}
            onPress={() => {
              onClose();
              openFeedback();
            }}
          />
        )}
      </View>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 6,
  },
  row: {
    minHeight: 60,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
  },
  rowPressed: {
    backgroundColor: reviewDesk.accentSoft,
  },
  rowIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentWash,
  },
  rowText: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
  },
  rowDescription: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 18,
  },
});
